import { Settings, SquarePen } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import {
  useGetMyProducts,
  useUpsertSellerInventory,
  useUpsertSellerProductConfiguration,
} from '~/api/my-products';
import { Modal } from '~/components/overlays';
import {
  Table,
  useTable,
  type SortDirection,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table';
import type {
  SellerProductFiltersInput,
  SellerProductInventoriesQuery,
  SellerProductInventory,
  SellerProductStatus,
  UnitCode,
} from '~/graphql/graphql';
import { currencyCodeToSymbolMap } from '~/utils/data';
import { extractGraphQLErrorMessages, type ClientError } from '~/utils';
import { ProductSettingModal } from '~/components/cards/ProductSettingModal';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { MyProductsFilter } from './MyProductsFilter';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { useDebounce } from '~/hooks/useDebounce';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';

type Filter = {
  field: string;
  operator: string;
  value: string | number;
};
type ReturnedSellerProductInventory = NonNullable<
  SellerProductInventoriesQuery['sellerProductInventories']['content'][0]
>;

export function CurrentProducts() {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<SellerProductFiltersInput>({});
  const [currentInventory, setCurrentInventory] =
    useState<SellerProductInventory | null>(null);

  const navigate = useNavigate();
  const { t } = useTranslation('my-product');

  const { data, isLoading, isLoadingMore, sentinelRef, hasMoreData } =
    usePaginatedQuery<
      SellerProductInventoriesQuery,
      ReturnedSellerProductInventory,
      SellerProductFiltersInput
    >({
      fetchHook: useGetMyProducts,
      select: (data) => data?.sellerProductInventories,
      options: { filters },
    });
  const {
    mutate: upsertSellerProductConfiguration,
    isPending: isUpsertConfigurationPending,
  } = useUpsertSellerProductConfiguration();

  const { mutate: upsertSellerInventory, isPending: isUpsertInventoryPending } =
    useUpsertSellerInventory();

  const updateFilterWithSearchTerm = (searchTerm: string) => {
    const updatedFilters = (filters.and || []).filter(
      (filter) => filter.or === undefined
    ); // remove old search term
    const trimmedSearchTerm = searchTerm.trim();
    if (trimmedSearchTerm) {
      updatedFilters.push({
        or: [
          { productName: { contains: trimmedSearchTerm } },
          { species: { contains: trimmedSearchTerm } },
          { grade: { contains: trimmedSearchTerm } },
          { productCode: { contains: trimmedSearchTerm } },
        ],
      });
    }
    setFilters({ and: updatedFilters });
  };

  const debouncedUpdateFilterWithSearchTerm = useDebounce(
    updateFilterWithSearchTerm,
    500
  );

  const handleFiltersChange = (newFilters: Filter[]) => {
    const updatedFilters: SellerProductFiltersInput = {
      and: [],
    };
    newFilters.forEach((filter) => {
      if (filter.field === 'dimensions') {
        const [thickness, width] = filter.value.toString().split('X');
        const nominalThickness = parseFloat(thickness);
        const nominalWidth = parseFloat(width);
        if (filter.operator === 'neq') {
          updatedFilters.and!.push({
            not: {
              and: [
                { nominalThicknessInches: { eq: nominalThickness } },
                { nominalWidthInches: { eq: nominalWidth } },
              ],
            },
          });
        } else {
          updatedFilters.and!.push({
            nominalThicknessInches: { [filter.operator]: nominalThickness },
          });
          updatedFilters.and!.push({
            nominalWidthInches: { [filter.operator]: nominalWidth },
          });
        }
      } else {
        let value = filter.value;
        if (typeof value === 'string') {
          value = value.trim();
        }
        updatedFilters.and!.push({
          [filter.field]: {
            [filter.operator]: value,
          },
        });
      }
    });

    // get existing search term filter if exists and add to updated filters so that search term filter can work together with other filters
    const existingSearchTermFilter = (filters.and || []).find(
      (filter) => filter.or !== undefined
    );
    if (existingSearchTermFilter) {
      updatedFilters.and!.push(existingSearchTermFilter);
    }
    setFilters(updatedFilters);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedUpdateFilterWithSearchTerm(value);
  };

  const handleTableSort = (key: string, direction: SortDirection) => {
    console.log('Sorted by', key, direction);
  };

  const mutationHandler = {
    onSuccess: () => {
      toast.success(t('messages.product_updated_successfully'));
      setShowSettingsModal(false);
    },
    onError: (error: ClientError) =>
      toast.error(extractGraphQLErrorMessages(error).join(', ')),
    onSettled: () => {
      tableControls.handleEditDone();
    },
  };

  const handleTableEditSubmit = (
    rowId: string,
    columnKey: string,
    newValue: string | number
  ) => {
    const inventory = data.find((inv) => inv?.id === rowId);
    const platformProductLengthId = inventory?.platformProductsLength.id ?? '';

    if (columnKey === 'stock') {
      const payload = {
        platformProductsLengthId: platformProductLengthId,
        quantity: { value: Number(newValue), unit: 'MBF' as const },
      };
      upsertSellerInventory(payload, mutationHandler);
      return;
    }

    const currentReplenishment =
      inventory?.sellerProductConfiguration.replenishmentRatePerWeek;
    const replenishmentRatePerWeek =
      columnKey === 'replenishmentRatePerWeek'
        ? {
            value: Number(newValue),
            unit: currentReplenishment?.unit as UnitCode,
          }
        : {
            value: currentReplenishment?.value ?? 0,
            unit: currentReplenishment?.unit as UnitCode,
          };

    const payload = {
      platformProductLengthId: platformProductLengthId,
      sellerProductConfigurationInput: {
        [columnKey]:
          columnKey !== 'replenishmentRatePerWeek'
            ? Number(newValue)
            : undefined,
        replenishmentRatePerWeek,
      },
    };
    upsertSellerProductConfiguration(payload, mutationHandler);
  };

  const tableControls = useTable({
    onSort: handleTableSort,
    onEditSubmit: handleTableEditSubmit,
  });

  const handleActionsClicked = (inventoryId: string, action: string) => {
    if (action === 'edit') {
      navigate(`/my-products/${inventoryId}/edit`);
      return;
    }
    const currentInventory = data.find((inv) => inv?.id === inventoryId);
    setCurrentInventory(currentInventory as SellerProductInventory);
    setShowSettingsModal(true);
  };

  const handleSaveSettings = (
    criticalStockLevel: number,
    lowStockLevel: number
  ) => {
    const currentReplenishment =
      currentInventory?.sellerProductConfiguration.replenishmentRatePerWeek;
    const currentUnit = (currentInventory?.sellerProductConfiguration.unit ??
      'MBF') as UnitCode;
    const payload = {
      platformProductLengthId:
        currentInventory?.platformProductsLength.id || '',
      sellerProductConfigurationInput: {
        criticalStockLevel: { value: criticalStockLevel, unit: currentUnit },
        lowStockLevel: { value: lowStockLevel, unit: currentUnit },
        replenishmentRatePerWeek: {
          value: currentReplenishment?.value ?? 0,
          unit: (currentReplenishment?.unit ?? currentUnit) as UnitCode,
        },
      },
    };
    upsertSellerProductConfiguration(payload, mutationHandler);
  };

  const columns: TableColumn[] = getTableColumns(t);
  const rows: TableRow[] = getTableRows(data, handleActionsClicked, t);

  return (
    <>
      <Modal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      >
        <ProductSettingModal
          initialCriticalStockLevel={
            currentInventory?.sellerProductConfiguration.criticalStockLevel
              .value || 0
          }
          initialLowStockLevel={
            currentInventory?.sellerProductConfiguration.lowStockLevel.value ||
            0
          }
          stockQuantity={currentInventory?.quantity?.value || 0}
          replenishmentRate={
            currentInventory?.sellerProductConfiguration
              ?.replenishmentRatePerWeek?.value || 0
          }
          onClose={() => setShowSettingsModal(false)}
          onSave={handleSaveSettings}
          isSaving={isUpsertConfigurationPending || isUpsertInventoryPending}
        />
      </Modal>

      <div className="bg-neut-bg-alt rounded-lg border border-neut-border shadow-sm">
        <div className="px-6 py-4 border-b border-neut-border flex items-center justify-between">
          <div>
            <h2 className="text-lg text-neut-prim font-semibold">
              {t('current_products.title')}
            </h2>
            <p className="text-xs text-neut-sec mt-1">
              {t('current_products.subtitle')}
            </p>
          </div>
        </div>

        <div className="w-full flex items-center justify-center md:justify-end px-1 md:px-3 py-4 ">
          <MyProductsFilter
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onFiltersChange={handleFiltersChange}
          />
        </div>

        <div className="overflow-x-auto ">
          <Table
            minWidth={55}
            rows={rows}
            columns={columns}
            tableControls={tableControls}
          />
          {isLoading || isLoadingMore ? (
            <PageLoadingSpinner name="products" />
          ) : null}
        </div>

        <div ref={sentinelRef} className="w-full py-2"></div>

        {!isLoading && hasMoreData === false && data.length > 0 && (
          <p className="w-full text-center py-2 text-neut-sec">
            {t('current_products.no_more_products')}
          </p>
        )}
      </div>
    </>
  );
}

function getTableRows(
  productsInventory: ReturnedSellerProductInventory[] | undefined,
  handleActionsClicked: (inventoryId: string, action: string) => void,
  t: TFunction
): TableRow[] {
  if (productsInventory == null) {
    return [];
  }
  return productsInventory.map(
    (inventory: ReturnedSellerProductInventory, idx: number) => ({
      visibility: {
        value: getVisibilityIndicator(
          inventory.sellerProductConfiguration.status,
          t
        ),
      },
      id: { value: inventory.id },
      productCode: {
        value: inventory.platformProductsLength?.platformProduct?.code || '',
      },
      productName: {
        value: inventory.platformProductsLength?.platformProduct?.name || '',
      },
      length: {
        value: inventory.platformProductsLength?.length?.displayValue || '',
      },
      pricePerUnit: {
        value: `${currencyCodeToSymbolMap[inventory.sellerProductConfiguration.currencyCode]}${inventory.sellerProductConfiguration.pricePerUnit}/${inventory.sellerProductConfiguration.unit}`,
        editValue: inventory.sellerProductConfiguration.pricePerUnit,
      },
      stock: {
        value: inventory.quantity.value.toLocaleString(),
        editValue: inventory.quantity.value,
      },
      status: { value: getSellerProductStatus(inventory, t) },
      replenishmentRatePerWeek: {
        value: `${inventory.sellerProductConfiguration.replenishmentRatePerWeek.value}/week`,
        editValue:
          inventory.sellerProductConfiguration.replenishmentRatePerWeek.value,
      },
      readyToShipDays: {
        value: inventory.sellerProductConfiguration.readyToShipDays,
        editValue: inventory.sellerProductConfiguration.readyToShipDays,
      },
      actions: {
        value: (
          <div className="flex gap-4 items-center justify-center">
            <button
              data-testid={`settings-button-${idx}`}
              className="cursor-pointer"
              onClick={() => handleActionsClicked(inventory.id, 'settings')}
            >
              <Settings size={18} />
            </button>
            <button
              data-testid={`edit-button-${idx}`}
              className="cursor-pointer"
              onClick={() => handleActionsClicked(inventory.id, 'edit')}
            >
              <SquarePen size={18} />
            </button>
          </div>
        ),
      },
    })
  );
}

function getSellerProductStatus(
  inventory: ReturnedSellerProductInventory,
  t: TFunction
): ReactNode {
  if (
    inventory.quantity.value >
    inventory.sellerProductConfiguration.lowStockLevel.value
  ) {
    return (
      <span className="text-sm font-medium text-success-700 bg-success-100 px-2 py-1 rounded-lg">
        {t('current_products.status.healthy')}
      </span>
    );
  }

  if (
    inventory.quantity.value >
    inventory.sellerProductConfiguration.criticalStockLevel.value
  ) {
    return (
      <span className="text-sm font-medium text-warning-600 bg-warning-100 px-2 py-1 rounded-lg">
        {t('current_products.status.low')}
      </span>
    );
  }

  return (
    <span className="text-sm font-medium text-error-500 bg-error-100 px-2 py-1 rounded-lg">
      {t('current_products.status.critical')}
    </span>
  );
}

function getTableColumns(t: TFunction): TableColumn[] {
  return [
    {
      key: 'visibility',
      header: t('current_products.table.headers.visibility'),
      width: 5,
    },
    {
      key: 'productCode',
      header: t('current_products.table.headers.product_code'),
      width: 6,
    },
    {
      key: 'productName',
      header: t('current_products.table.headers.product'),
      width: 7,
    },
    { key: 'length', header: t('current_products.table.headers.length') },
    {
      key: 'pricePerUnit',
      header: t('current_products.table.headers.price'),
      width: 8,
      isEditable: true,
      editableType: 'number',
    },
    {
      key: 'stock',
      header: t('current_products.table.headers.stock'),
      width: 8,
      isEditable: true,
      editableType: 'number',
    },
    { key: 'status', header: t('current_products.table.headers.status') },
    {
      key: 'replenishmentRatePerWeek',
      header: t('current_products.table.headers.replenishment_rate'),
      width: 8,
      isEditable: true,
      editableType: 'number',
    },
    {
      key: 'readyToShipDays',
      header: t('current_products.table.headers.ready_to_ship'),
      width: 5,
      isEditable: true,
      editableType: 'number',
    },
    { key: 'actions', header: t('current_products.table.headers.actions') },
  ];
}

function getVisibilityIndicator(
  status: SellerProductStatus | null | undefined,
  t: TFunction
): ReactNode {
  if (!status) return t('current_products.status.na');
  const STATUS_COLOURS: Record<SellerProductStatus, string> = {
    VISIBLE_TO_BUYERS: 'bg-success-500',
    VISIBLE_TO_MY_BUYERS: 'bg-status-info-600',
    INACTIVE: 'bg-error-500',
  };
  return (
    <div className="w-full flex justify-center">
      <span
        className={`inline-block w-2.5 h-2.5 rounded-full ${STATUS_COLOURS[status]}`}
        title={t(`edit_product.options.status.${status.toLowerCase()}`)}
        role="img"
        aria-label={t(`edit_product.options.status.${status.toLowerCase()}`)}
      />
    </div>
  );
}
